import { ApiError, getFileExtension, getFileNameFromPath } from '@gorila-shared-ui/components';
import * as uuid from 'uuid';
import {
  DriverStatus,
  FleetDriver,
  FleetDriverDetail,
  FleetDriverRequest,
  UpdateFleetDriverRequest,
} from '../types/drivers';
import {
  FleetAsset,
  FleetAssetDocumentListRequest,
  FleetAssetRefuelRequest,
  FleetAssetRequest,
  FleetDriverPhotoDetail,
  FleetEditMaintenanceRuleRequest,
  FleetJobType,
  FleetMaintenanceRule,
  FleetMaintenanceRuleRequest,
  FleetMaintenanceType,
  FleetMaintenanceTypeRequest,
  FleetOperational,
  FleetService,
  FleetServiceJobRequest,
  FleetServicesRequest,
  FleetServiceStatus,
  FleetTriggerType,
} from '../types/fleet';
import { OperationalReport } from '../types/reports';
import { AssetType } from './../types/asset';
import { deleteRequest, getRequest, postRequest, uploadRequest } from './api';
import { fleetUrls } from './urls/fleetUrls';

export const getFleetMaintenanceTypes = async ({
  page,
  clientId,
  subClientId,
  search = '',
  allItems = false,
}: {
  page: number;
  clientId: string;
  subClientId: string;
  search?: string;
  allItems?: boolean;
}): Promise<{ types?: FleetMaintenanceType[]; hasNext?: boolean; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, page, allItems, search };
  const response = await getRequest(fleetUrls.maintenance.types.list, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    types: response?.data?.items,
    hasNext: response?.data?.hasNext,
    error: response?.error,
  };
};

export const createFleetMaintenanceType = async (
  data: FleetMaintenanceTypeRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.maintenance.types.create, {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const updateFleetMaintenanceType = async (
  maintenanceTypeId: string,
  data: FleetMaintenanceTypeRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.maintenance.types.update(maintenanceTypeId), {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const getFleetMaintenanceRules = async ({
  page,
  clientId,
  subClientId,
  maintenanceTypeId,
  triggerType,
  search = '',
  allItems = false,
}: {
  page: number;
  clientId: string;
  subClientId: string;
  maintenanceTypeId?: string;
  triggerType?: FleetTriggerType;
  search?: string;
  allItems?: boolean;
}): Promise<{
  configurations?: FleetMaintenanceRule[];
  hasNext?: boolean;
  error?: ApiError;
  total?: number;
}> => {
  const queryParams = { clientId, subClientId, page, allItems, search, maintenanceTypeId, triggerType };
  const response = await getRequest(fleetUrls.maintenance.rules.list, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    configurations: response?.data?.items,
    hasNext: response?.data?.hasNext,
    error: response?.error,
  };
};

export const createFleetMaintenanceRule = async (
  data: FleetMaintenanceRuleRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.maintenance.rules.create, {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const updateFleetMaintenanceRule = async (
  configurationId: string,
  data: FleetEditMaintenanceRuleRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.maintenance.rules.update(configurationId), {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const getFleetServices = async ({
  page,
  clientId,
  subClientId,
  configurationId,
  status,
  search = '',
}: {
  page: number;
  clientId: string;
  subClientId: string;
  configurationId?: string;
  status?: string;
  search?: string;
}): Promise<{ services?: FleetService[]; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, configurationId, page, status, search };
  const response = await getRequest(fleetUrls.services.list, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    services: response?.data?.items,
    error: response?.error,
  };
};

export const createFleetServices = async (
  data: FleetServicesRequest,
): Promise<{ createdIds?: string[]; missingAssets?: string[]; conflictedAssets?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.services.create, {
    body: data,
  });

  return {
    createdIds: response?.data?.createdIds,
    missingAssets: response?.data?.missingAssets,
    conflictedAssets: response?.data?.conflictedAssets,
    error: response?.error,
  };
};

export const cancelFleetService = async ({
  serviceId,
  clientId,
  subClientId,
}: {
  serviceId: string;
  clientId: string;
  subClientId: string;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.services.cancel(serviceId), {
    body: {
      clientId,
      subClientId,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const getFleetAssetServices = async ({
  page,
  clientId,
  subClientId,
  assetId,
  configurationId,
  status,
}: {
  page: number;
  clientId: string;
  assetId: string;
  subClientId: string;
  configurationId?: string;
  status?: FleetServiceStatus;
}): Promise<{ services?: FleetService[]; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, page, configurationId, status };
  const response = await getRequest(fleetUrls.services.asset.list(assetId), {
    queryParams,
  });

  return {
    total: response?.data?.total,
    services: response?.data?.items,
    error: response?.error,
  };
};

export const createFleetServiceJob = async (
  assetId: string,
  data: FleetServiceJobRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.services.asset.create(assetId), {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const createFleetServiceJobEvidences = async ({
  assetId,
  clientId,
  subClientId,
  jobType,
  files,
}: {
  assetId: string;
  clientId: string;
  subClientId: string;
  jobType: FleetJobType;
  files: File[];
}): Promise<{ paths?: string[]; error?: ApiError }> => {
  const promises = files.map((file) => {
    const myId = uuid.v4();
    const extension = getFileExtension(file.name);
    const fileName = getFileNameFromPath(file.name);
    const baseName = fileName.slice(0, -extension.length);

    return uploadRequest(`public/myFleet/${clientId}/${assetId}/${jobType}/${myId}.${baseName}${extension}`, file, {
      contentType: file.type,
      metadata: { clientId, subClientId },
    });
  });

  return Promise.all(promises)
    .then((responses) => {
      const paths = responses.map((response) => response.data?.key ?? '');
      return {
        paths,
      };
    })
    .catch((error) => {
      return {
        error,
      };
    });
};

export const getFleetAssets = async ({
  page,
  clientId,
  subClientId,
  brandId,
  subBrandId,
  assetTypes,
  search = '',
}: {
  page: number;
  clientId: string;
  subClientId: string;
  brandId?: string;
  subBrandId?: string;
  search?: string;
  assetTypes?: AssetType[];
}): Promise<{ assets?: FleetAsset[]; hasNext?: boolean; error?: ApiError; total?: number }> => {
  let path = fleetUrls.assets.list;
  const queryParams = { clientId, subClientId, page, brandId, subBrandId, search };

  if (assetTypes) {
    path += '?' + assetTypes.map((assetType) => `assetTypes=${assetType}`).join('&');
  }

  const response = await getRequest(path, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    assets: response?.data?.items,
    hasNext: response?.data?.hasNext,
    error: response?.error,
  };
};

export const updateFleetAsset = async (
  assetId: string,
  data: FleetAssetRequest,
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.assets.update(assetId), {
    body: data,
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const updateFleetAssetDocuments = async ({
  assetId,
  clientId,
  subClientId,
  documents,
}: {
  assetId: string;
  clientId: string;
  subClientId: string;
  documents: FleetAssetDocumentListRequest;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.assets.updateDocuments(assetId), {
    body: {
      clientId,
      subClientId,
      documents,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const createFleetAssetDocument = async ({
  assetId,
  clientId,
  subClientId,
  file,
}: {
  assetId: string;
  clientId: string;
  subClientId: string;
  file: File;
}): Promise<{ path?: string; error?: ApiError }> => {
  const myId = uuid.v4();
  const extension = getFileExtension(file.name);
  const fileName = getFileNameFromPath(file.name);
  const baseName = fileName.slice(0, -extension.length);

  const { data, error } = await uploadRequest(
    `public/myFleet/${clientId}/${assetId}/documents/${myId}.${baseName}${extension}`,
    file,
    {
      contentType: file.type,
      metadata: { clientId, subClientId },
    },
  );

  return {
    path: data?.key,
    error,
  };
};

export const getFleetDrivers = async ({
  page,
  clientId,
  subClientId,
  status,
  search = '',
}: {
  page: number;
  clientId: string;
  subClientId: string;
  search?: string;
  status?: DriverStatus;
}): Promise<{ drivers?: FleetDriver[]; hasNext?: boolean; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, page, status, search };

  const response = await getRequest(fleetUrls.drivers.list, {
    queryParams,
  });

  return {
    total: response?.data?.total,
    drivers: response?.data?.items,
    hasNext: response?.data?.hasNext,
    error: response?.error,
  };
};

export const getFleetDriver = async ({
  driverId,
  clientId,
  subClientId,
}: {
  driverId: string;
  clientId: string;
  subClientId: string;
}): Promise<{ driver?: FleetDriverDetail; error?: ApiError }> => {
  const queryParams = { clientId, subClientId };

  const response = await getRequest(fleetUrls.drivers.detail(driverId), {
    queryParams,
  });

  return {
    driver: response?.data,
    error: response?.error,
  };
};

export const createFleetDriver = async ({
  data,
  clientId,
  subClientId,
}: {
  data: FleetDriverRequest;
  clientId: string;
  subClientId: string;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.drivers.create, {
    body: {
      clientId,
      subClientId,
      ...data,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const updateFleetDriver = async ({
  clientId,
  data,
  driverId,
  subClientId,
}: {
  driverId: string;
  data: UpdateFleetDriverRequest;
  clientId: string;
  subClientId: string;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.drivers.update(driverId), {
    body: {
      clientId,
      subClientId,
      ...data,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const createFleetDriverPhoto = async ({
  clientId,
  subClientId,
  file,
}: {
  clientId: string;
  subClientId: string;
  file: File;
}): Promise<{ key?: string; error?: ApiError }> => {
  const myId = uuid.v4();
  const extension = getFileExtension(file.name);
  const fileName = getFileNameFromPath(file.name);
  const baseName = fileName.slice(0, -extension.length);

  const response = await uploadRequest(`public/photoDriverFiles/unknown/${myId}.${baseName}${extension}`, file, {
    contentType: file.type,
    metadata: { clientId, subClientId },
  });

  return {
    key: response?.data?.key,
    error: response?.error,
  };
};

export const getFleetDriverPhotos = async ({
  page,
  clientId,
  subClientId,
  driverId,
}: {
  page: number;
  clientId: string;
  subClientId: string;
  driverId: string;
}): Promise<{ photos?: FleetDriverPhotoDetail[]; hasNext?: boolean; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, page };
  const response = await getRequest(fleetUrls.drivers.photos.list(driverId), {
    queryParams,
  });

  return {
    total: response?.data?.total,
    photos: response?.data?.items,
    hasNext: response?.data?.hasNext,
    error: response?.error,
  };
};

export const verifyFleetDriverPhoto = async ({
  clientId,
  subClientId,
  fileKey,
}: {
  clientId: string;
  subClientId: string;
  fileKey: string;
}): Promise<{ driver?: FleetDriver; error?: ApiError }> => {
  const queryParams = { clientId, subClientId, s3Key: fileKey };
  const response = await getRequest(
    fleetUrls.drivers.photos.verify,
    {
      queryParams,
    },
    undefined,
    undefined,
    true,
  );

  return {
    driver: response?.data,
    error: response?.error,
  };
};

export const updateFleetDriverSubClients = async ({
  clientId,
  photo,
  driverId,
  subClientId,
}: {
  driverId: string;
  photo: string;
  clientId: string;
  subClientId: string;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.drivers.subClients.update(driverId), {
    body: {
      clientId,
      subClientId,
      photo,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const createFleetAssetRefuel = async ({
  assetId,
  data,
  clientId,
  subClientId,
}: {
  assetId: string;
  data: FleetAssetRefuelRequest;
  clientId: string;
  subClientId: string;
}): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(fleetUrls.assets.refuel.create(assetId), {
    body: {
      clientId,
      subClientId,
      ...data,
    },
  });

  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const getFleetOperationalReportFiles = async ({
  clientId,
  page,
  subClientId,
  assetId,
}: {
  assetId?: string;
  page: number;
  clientId: string;
  subClientId: string;
}): Promise<{ reports?: OperationalReport[]; error?: ApiError; total?: number }> => {
  const response = await getRequest(fleetUrls.operational.reports.list, {
    queryParams: { clientId, subClientId, page, assetId },
  });

  return {
    total: response?.data?.total,
    reports: response?.data?.items,
    error: response?.error,
  };
};

export const getFleetOperationalReportFile = async ({
  reportId,
  clientId,
  subClientId,
}: {
  reportId: string;
  clientId: string;
  subClientId: string;
}): Promise<{ url?: string; error?: ApiError }> => {
  const response = await getRequest(fleetUrls.operational.reports.download(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const deleteFleetOperationalReportFile = async ({
  reportId,
  clientId,
  subClientId,
}: {
  reportId: string;
  clientId: string;
  subClientId: string;
}): Promise<{ error?: ApiError }> => {
  const response = await deleteRequest(fleetUrls.operational.reports.delete(reportId), {
    queryParams: { clientId, subClientId },
  });

  return {
    error: response?.error,
  };
};

export const requestFleetOperationalReportFileByGroup = async ({
  groupId,
  startDate,
  endDate,
  clientId,
  subClientId,
}: {
  groupId: string;
  startDate: number;
  endDate?: number;
  clientId: string;
  subClientId: string;
}): Promise<{ error?: ApiError }> => {
  const response = await postRequest(fleetUrls.operational.reports.create, {
    body: { clientId, subClientId, groupId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const requestFleetOperationalReportFileByAsset = async ({
  assetId,
  startDate,
  endDate,
  clientId,
  subClientId,
}: {
  assetId: string;
  startDate: number;
  endDate?: number;
  clientId: string;
  subClientId: string;
}): Promise<{ error?: ApiError }> => {
  const response = await postRequest(fleetUrls.operational.reports.create, {
    body: { clientId, subClientId, assetId, startDate, endDate },
  });

  return {
    error: response?.error,
  };
};

export const getFleetOperationalReportByAsset = async ({
  startDate,
  clientId,
  subClientId,
  assetId,
  endDate,
}: {
  startDate: number;
  clientId: string;
  assetId: string;
  subClientId: string;
  endDate: number;
}): Promise<{ data?: FleetOperational; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, startDate, endDate };
  const response = await getRequest(fleetUrls.operational.listByAsset(assetId), {
    queryParams,
  });

  return {
    data: response?.data,
    error: response?.error,
  };
};

export const getFleetOperationalReportByGroup = async ({
  startDate,
  clientId,
  subClientId,
  groupId,
  endDate,
}: {
  startDate: number;
  clientId: string;
  groupId: string;
  subClientId: string;
  endDate: number;
}): Promise<{ data?: FleetOperational[]; error?: ApiError; total?: number }> => {
  const queryParams = { clientId, subClientId, startDate, endDate };
  const response = await getRequest(fleetUrls.operational.listByGroup(groupId), {
    queryParams,
  });

  return {
    data: response?.data,
    error: response?.error,
  };
};
